import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { Button } from 'reactstrap';
import each from 'lodash/each';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';

import env from 'Env';
import { countryCode } from 'Helpers/Geo/data';
import { saveBilling, refreshAuth } from 'Redux/ducks/auth';
import CountryNames from 'Helpers/Geo/CountryNameLocales';
import debug from 'Helpers/debug';
import getAddressHelpers from 'Components/Account/AddressHelpers';
import store from 'Redux/store';

const messages = defineMessages({
  selectAdministrativeArea: {
    id: 'address-form.dropdownDefault',
    defaultMessage: 'Choose',
  },
  addressPlaceholder: {
    id: 'address-form.addressPlaceholder',
    defaultMessage: 'Start typing your address',
  },
});

const AddressForm = ({
  userBilling,
  billingFields,
  onComplete,
  onCancel,
  intl: { formatMessage },
  buttonLabel,
}) => {
  const [billingValues, setBillingValues] = useState({
    address1: '',
    administrativeArea: '',
    countryCode,
    customerName: '',
    locality: '',
    postalCode: '',
    ...userBilling,
  });
  const [billingErrors, setBillingErrors] = useState({});
  const [hasValidated, setHasValidated] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [scriptHasLoaded, setScriptHasLoaded] = useState(false);

  const searchField = useRef();
  const address2Field = useRef();

  const {
    administrativeAreaArray,
    postCodeRegex,
    postCodeFormatter,
    fieldLabels,
  } = getAddressHelpers(billingValues.countryCode || countryCode);

  const hasAdminstrativeAreaList = !!administrativeAreaArray.length;

  const validate = (update) => {
    setHasValidated(true);
    const errors = {};

    const fieldsToValidate = update || billingValues;

    billingFields.forEach((fieldName) => {
      if (!fieldsToValidate[fieldName] || typeof fieldsToValidate[fieldName] !== 'string') {
        errors[fieldName] = true;
      }

      if (fieldName === 'postalCode' && (!fieldsToValidate.postalCode || !fieldsToValidate.postalCode.match(postCodeRegex))) {
        errors[fieldName] = true;
      }
    });

    setBillingErrors(errors);
    return isEmpty(errors);
  };

  const isValid = isEmpty(billingErrors);

  const setBillingValue = (e) => {
    const { target: { name } } = e;
    let { target: { value } } = e;

    if (name === 'postalCode') {
      value = postCodeFormatter(value);
    }

    const update = { ...billingValues, [name]: value };

    setBillingValues(update);
    setHasSaved(false);
    if (hasValidated) {
      validate(update); // billingValues state has not updated so pass in new address directly
    }
  };

  const resetAddressFields = (e) => {
    const { target: { name, value } } = e;
    if (billingValues[name] !== value) {
      setBillingValues({
        customerName: billingValues.customerName,
        address1: '',
        address2: '',
        locality: '',
        administrativeArea: '',
        postalCode: '',
        [name]: value,
      });
      setBillingErrors({});
    }
    setHasSaved(false);
  };

  const submit = async () => {
    if (validate()) {
      const response = await saveBilling(billingValues);
      if (response.ok) {
        setHasSaved(true);
        store.dispatch(refreshAuth());

        if (onComplete) {
          onComplete(billingValues);
        }
      }
    }
  };

  const fillInAddress = (place) => {
    if (!place) {
      return;
    }

    debug('Raw google place address', place);

    const values = {};

    each(place.address_components, (component) => {
      const componentType = component.types[0];

      switch (componentType) {
        case 'street_number':
          values.address1 = `${component.long_name} ${values.address1 || ''}`.trim();
          break;

        case 'route':
          values.address1 = `${values.address1 || ''} ${component.long_name}`.trim();
          break;

        case 'postal_code':
          values.postalCode = `${component.long_name}${values.postalCode || ''}`;
          break;

        case 'postal_code_suffix':
          values.postalCode = `${values.postalCode}-${component.long_name}`;
          break;

        case 'locality':
        case 'postal_town':
          values.locality = component.long_name;
          break;

        case 'administrative_area_level_2':
        case 'administrative_area_level_1':
          if (administrativeAreaArray.map(a => a.value).includes(component.long_name)) {
            values.administrativeArea = component.long_name;
          } else if (administrativeAreaArray.map(a => a.value).includes(component.short_name)) {
            values.administrativeArea = component.short_name;
          } else if (!values.administrativeArea) {
            values.administrativeArea = component.short_name;
          }
          break;

        default:
          break;
      }
    });

    if (searchField.current) {
      searchField.current.value = '';
    }
    setHasSaved(false);

    debug('Parsed google place address', values);

    const update = {
      customerName: billingValues.customerName,
      countryCode: billingValues.countryCode || countryCode,
      ...pick(values, billingFields),
    };

    setBillingValues(update);
    validate(update);

    if (address2Field.current) {
      address2Field.current.setAttribute('autocomplete', 'off');
      address2Field.current.focus();
    }
  };

  useEffect(() => {
    if (!window.google || !window.google.maps) {
      const scriptTag = document.createElement('script');
      scriptTag.src = `https://maps.googleapis.com/maps/api/js?key=${env.gcp.placesPublicKey}&libraries=places&v=weekly&`;
      scriptTag.addEventListener('load', () => setScriptHasLoaded(true));
      document.body.appendChild(scriptTag);
    }
  }, []);

  useEffect(() => {
    if ((scriptHasLoaded || (window.google && window.google.maps)) && searchField.current) {
      const autoComplete = new window.google.maps.places.Autocomplete(searchField.current, {
        componentRestrictions: { country: [billingValues.countryCode] },
        fields: ['address_components'],
        types: ['address'],
      });
      autoComplete.addListener('place_changed', () => fillInAddress(autoComplete.getPlace()));
    }
  }, [scriptHasLoaded, searchField.current, billingValues]); // eslint-disable-line react-hooks/exhaustive-deps,max-len

  const countryOptions = Object.keys(CountryNames)
    .filter(value => !['A1', 'AP', 'EU', 'E1', 'A2', 'O1'].includes(value))
    .map(value => ({ value, label: formatMessage(CountryNames[value]) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <React.Fragment>
      <div className="address-form-billing-details-title">
        <FormattedMessage id="address-form.billing-address-title" defaultMessage="Billing Name & Address" />
      </div>
      <div className="address-form-container">
        <div className="address-form">
          { billingFields.includes('customerName') &&
            <div className="form-group address-fields">
              <label htmlFor="customerName">
                <FormattedMessage id="address-form.customerName-field" defaultMessage="Your Name" />*
              </label>
              <input
                type="text"
                id="customerName"
                name="customerName"
                autoComplete="name"
                value={billingValues.customerName}
                className={classNames('form-control', { 'input-error': billingErrors.customerName })}
                onChange={setBillingValue}
              />
              {billingErrors.customerName &&
                <div className="address-field-error">
                  <FormattedMessage id="address-form.customerName-selecterror" defaultMessage="Please enter your name" />
                </div>
              }
            </div>
          }

          <div className="form-group autocomplete-wrapper">
            <label htmlFor="addressSearch">
              <FormattedMessage id="address-form.addressSearch-field" defaultMessage="Billing Address Search" />
            </label>
            <input
              ref={searchField}
              type="text"
              id="addressSearch"
              name="addressSearch"
              autoComplete="off"
              className="form-control"
              placeholder={`${formatMessage(messages.addressPlaceholder)}...`}
            />
          </div>

          <div className="address-search-separator" />
          <div className="manual-address-text">
            <FormattedMessage id="address-form.search-separator" defaultMessage="Or enter your address manually" />
          </div>

          <div className="manual-form-container">
            { billingFields.includes('countryCode') &&
              <div className="form-group country-fields">
                <label htmlFor="countryCode">
                  <FormattedMessage id="address-form.countryCode-field" defaultMessage="Country" />*
                </label>
                <select
                  required
                  id="countryCode"
                  name="countryCode"
                  className={classNames('form-control', { 'input-error': billingErrors.countryCode })}
                  autoComplete="country"
                  defaultValue={billingValues.countryCode || countryCode}
                  onChange={resetAddressFields}
                >
                  {countryOptions.map(country =>
                    <option key={country.value} value={country.value}>{country.label}</option>,
                  )}
                </select>
                {billingErrors.countryCode &&
                  <div className="address-field-error">
                    <FormattedMessage id="address-form.countryCode-error" defaultMessage="Please choose a valid country" />
                  </div>
                }
              </div>
            }

            { billingFields.includes('address1') &&
              <React.Fragment>
                <div className="form-group address-fields-one">
                  <label htmlFor="address1">
                    { formatMessage(fieldLabels.address1) }*
                    <small>
                      { formatMessage(fieldLabels.address1Sub) }
                    </small>
                  </label>
                  <input
                    required
                    type="text"
                    id="address1"
                    name="address1"
                    autoComplete="address-line1"
                    value={billingValues.address1}
                    className={classNames('form-control', { 'input-error': billingErrors.address1 })}
                    onChange={setBillingValue}
                  />
                  {billingErrors.address1 &&
                    <div className="address-field-error">
                      <FormattedMessage id="address-form.address1-selecterror" defaultMessage="Please enter a valid address" />
                    </div>
                  }
                </div>

                <div className="form-group address-fields-two">
                  <label htmlFor="address2">
                    { formatMessage(fieldLabels.address2) }
                    <small>
                      { formatMessage(fieldLabels.address2Sub) }
                    </small>
                  </label>
                  <input
                    ref={address2Field}
                    type="text"
                    id="address2"
                    name="address2"
                    autoComplete="address-line2"
                    value={billingValues.address2}
                    className={classNames('form-control', { 'input-error': billingErrors.address2 })}
                    onChange={setBillingValue}
                  />
                </div>
              </React.Fragment>
            }

            { billingFields.includes('locality') &&
              <div className="form-group city-fields">
                <label htmlFor="locality">
                  { formatMessage(fieldLabels.locality) }*
                </label>
                <input
                  required
                  type="text"
                  id="locality"
                  name="locality"
                  autoComplete="address-level2"
                  value={billingValues.locality}
                  className={classNames('form-control', { 'input-error': billingErrors.locality })}
                  onChange={setBillingValue}
                />
                {billingErrors.locality &&
                  <div className="address-field-error">
                    <FormattedMessage id="address-form.locality-selecterror" defaultMessage="Please enter a valid {localityLabel}" values={{ localityLabel: formatMessage(fieldLabels.locality) }} />
                  </div>
                }
              </div>
            }

            { !!(billingFields.includes('administrativeArea') && hasAdminstrativeAreaList) &&
              <div className="form-group area-fields">
                <label htmlFor="administrativeArea">
                  { formatMessage(fieldLabels.administrativeArea) }*
                </label>
                <select
                  required
                  id="administrativeArea"
                  name="administrativeArea"
                  autoComplete="address-level1"
                  defaultValue={billingValues.administrativeArea}
                  className={classNames('form-control', { 'input-error': billingErrors.administrativeArea })}
                  onChange={setBillingValue}
                >
                  <option>
                    { formatMessage(messages.selectAdministrativeArea) }&nbsp;
                    { formatMessage(fieldLabels.administrativeArea) }...
                  </option>
                  {administrativeAreaArray.map(area =>
                    <option key={area.value} value={area.value}>{area.label}</option>,
                  )}
                </select>
                {billingErrors.administrativeArea &&
                  <div className="address-field-error">
                    <FormattedMessage id="address-form.administrativeArea-selecterror" defaultMessage="Please choose a valid {areaLabel}" values={{ areaLabel: formatMessage(fieldLabels.administrativeArea) }} />
                  </div>
                }
              </div>
            }

            { !!(billingFields.includes('administrativeArea') && !hasAdminstrativeAreaList) &&
              <div className="form-group area-fields">
                <label htmlFor="administrativeArea">
                  { formatMessage(fieldLabels.administrativeArea) }*
                </label>
                <input
                  required
                  type="text"
                  id="administrativeArea"
                  name="administrativeArea"
                  autoComplete="billing address-level1"
                  value={billingValues.administrativeArea}
                  className={classNames('form-control', { 'input-error': billingErrors.administrativeArea })}
                  onChange={setBillingValue}
                />
                {billingErrors.administrativeArea &&
                  <div className="address-field-error">
                    <FormattedMessage id="address-form.administrativeArea-error" defaultMessage="Please enter a valid {areaLabel}" values={{ areaLabel: formatMessage(fieldLabels.administrativeArea) }} />
                  </div>
                }
              </div>
            }

            { billingFields.includes('postalCode') &&
              <div className="form-group post-code-fields">
                <label htmlFor="postalCode">
                  { formatMessage(fieldLabels.postalCode) }*
                </label>
                <input
                  required
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  autoComplete="postal-code"
                  value={billingValues.postalCode}
                  className={classNames('form-control', { 'input-error': billingErrors.postalCode })}
                  onChange={setBillingValue}
                />
                {billingErrors.postalCode &&
                  <div className="address-field-error">
                    <FormattedMessage id="address-form.postalCode-error" defaultMessage="Please enter a valid {postCodeLabel}" values={{ postCodeLabel: formatMessage(fieldLabels.postalCode) }} />
                  </div>
                }
              </div>
            }
          </div>
        </div>

        <div className="address-form-buttons">
          <Button color="primary" className="complete-button" disabled={!isValid} onClick={submit}>
            { buttonLabel || <FormattedMessage id="address-form.sumbit" defaultMessage="Save Billing Details" /> }
          </Button>
          {onCancel &&
            <Button onClick={onCancel}><FormattedMessage id="address-form.cancel" defaultMessage="Cancel" /></Button>
          }
          {hasSaved &&
            <div className="address-success"><FormattedMessage id="address-form.success" defaultMessage="Saved" /></div>
          }
        </div>
      </div>
    </React.Fragment>
  );
};

AddressForm.propTypes = {
  userBilling: PropTypes.shape({}),
  billingFields: PropTypes.arrayOf(PropTypes.string),
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
  buttonLabel: PropTypes.element,
};

AddressForm.defaultProps = {
  userBilling: {},
  billingFields: [],
  onComplete: () => {},
  onCancel: null,
  buttonLabel: null,
};

export default injectIntl(AddressForm);
